// course-by-id.ts — модуль для работы с course_id => permalink

declare global {
  interface Window {
    ajaxurl?: string;
    courseByIdNonce?: string;
  }
}

export type CourseIdMap = Record<string, string>;

const STORAGE_KEY = 'courseIdMap';
const STORAGE_TIMESTAMP_KEY = 'courseIdMapTimestamp';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 час

/**
 * Проверяет, нужно ли обновлять кэш (старше 1 часа)
 */
function isCacheExpired(): boolean {
  const ts = localStorage.getItem(STORAGE_TIMESTAMP_KEY);
  if (!ts) return true;
  const now = Date.now();
  return now - parseInt(ts, 10) > CACHE_TTL_MS;
}

/**
 * Загружает карту course_id => permalink через AJAX и сохраняет в localStorage
 */
export async function fetchAndStoreCourseIdMap(): Promise<CourseIdMap | null> {
  if (!window.ajaxurl || !window.courseByIdNonce) return null;
  try {
    const formData = new FormData();
    formData.append('action', 'get_course_link_by_id');
    formData.append('security', window.courseByIdNonce);
    // Без course_id — получим весь массив
    const response = await fetch(window.ajaxurl, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    //console.log('[fetchAndStoreCourseIdMap] Ответ сервера:', data);
    const map = data?.data?.data;
    if (data.success && map && typeof map === 'object') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
      localStorage.setItem(STORAGE_TIMESTAMP_KEY, String(Date.now()));
      //console.log('[fetchAndStoreCourseIdMap] Сохраняем в localStorage:', map);
      return map;
    }
    return null;
  } catch (e) {
    console.error('[fetchAndStoreCourseIdMap] Ошибка:', e);
    return null;
  }
}

/**
 * Получает карту course_id => permalink из localStorage
 */
export function getCourseIdMap(): CourseIdMap {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    //console.warn('[getCourseIdMap] Нет данных в localStorage');
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    //console.log('[getCourseIdMap] Прочитано из localStorage:', parsed);
    return parsed;
  } catch (e) {
    //console.error('[getCourseIdMap] Ошибка парсинга:', e);
    return {};
  }
}

/**
 * Получает permalink по course_id
 */
export function getCourseLinkById(courseId: string | number): string | null {
  const map = getCourseIdMap();
  const key = String(courseId);
  if (map[key]) {
    //console.log('[getCourseLinkById] Найдено:', key, map[key]);
    return map[key];
  }
  //console.warn('[getCourseLinkById] Не найдено:', key);
  return null;
}

// Инициализация: если данных нет или кэш устарел — загружаем
if (!localStorage.getItem(STORAGE_KEY) || isCacheExpired()) {
  fetchAndStoreCourseIdMap();
}
